<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import MatrixChoiceItem from '@/components/ConsultationSummary/QuestionResults/MatrixChoiceItem.vue';

import { Constants } from '@/constants';
import type { Locales, SummaryFormStatLevelItemDto, SummaryFormStatLevelDto } from '@/services/types/api.types';
import { useSummariesStore } from '@/stores/summaries.store';

// STORE
const summariesStore = useSummariesStore();
const { formStats } = storeToRefs(summariesStore);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  formId: number;
}>();

// CONST
const { t } = useI18n();

// COMPUTED
const levels = computed((): SummaryFormStatLevelItemDto[] | null => {
  const formStat = formStats?.value?.get(props.formId);
  switch (formStat?.type) {
    case Constants.Form.Type.Matrix: {
      const stats = formStat?.stats as unknown as SummaryFormStatLevelDto;
      return stats?.levels ? stats.levels : null;
    }
    default:
      return null;
  }
});

const isConfigured = computed(
  (): boolean => levels.value?.some((level: SummaryFormStatLevelItemDto) => level.level_choices.length >= 2) ?? false,
);
</script>
<template>
  <div class="form-matrix-question-results">
    <div v-if="isConfigured">
      <MatrixChoiceItem
        v-for="level in levels"
        :key="level.id"
        :level="level"
        :language-enabled="props.languageEnabled"
        :form-id="props.formId"
      />
    </div>
    <div v-else class="text">
      {{ t('form.summary.status.not-configured') }}
    </div>
  </div>
</template>
<style lang="scss">
.form-matrix-question-results {
  display: flex;
  flex-direction: column;
  gap: $space-md;

  .text {
    color: $color-neutral-grey-400;
    font-size: $small-text;
  }
}
</style>
